import { useMediaQuery, Box, Flex, Text } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { ChakraBox } from "ui/chakra-framer";
import { BackgroundCircle } from "./background-circle";
import { CircleProgress } from "./circle-progress";
import { BoostedText } from "./boosted-text";

export function MatchBoosterAnimation() {
  const start = 52;
  const end = 67;
  const animationDuration = 65;

  const [counter, setCounter] = useState<number>(start);
  const [progressActive, setProgressActive] = useState<boolean>(false);
  const [progressComplete, setProgressComplete] = useState<boolean>(false);

  const [isMobile] = useMediaQuery("(max-width: 800px)");
  const size = isMobile ? 300 : 400;
  const strokeWidth = isMobile ? 18 : 24;

  useEffect(() => {
    if (progressActive && counter < end) {
      // Increment progress animation on a slight bell curve
      const timer = setInterval(() => {
        setCounter((counter) => counter + 1);
      }, animationDuration + (counter - start) / 2);

      return () => {
        clearInterval(timer);
      };
    }

    if (progressActive && counter >= end) {
      // Stop counter and signal the progress is complete for the flicker text.
      setProgressActive(false);
      setProgressComplete(true);
    }
  }, [progressActive, counter, start, end]);

  return (
    <Box aria-label={`${end}% boosted match rate.`} pos="relative">
      <AnimatePresence>
        <ChakraBox
          display="flex"
          w="100%"
          p="15px" // Needs 15px of padding for 3px border + 12px offset below so outline doesn't overflow
          alignItems="center"
          justifyContent="center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.6 } }}
          onAnimationComplete={() => {
            // Pause slightly between fade-in before circle progress animation begins.
            setTimeout(() => {
              setProgressActive(true);
            }, 400);
          }}
        >
          <Box
            pos="relative"
            boxSize={size}
            borderRadius="full"
            outline="3px solid"
            // Mobile screens override the outline borderRadius making it look like a square, so the border is hidden altogether on mobile
            outlineColor={{ base: "transparent", md: "base.background" }}
            outlineOffset={12}
          >
            <BackgroundCircle size={size} strokeWidth={strokeWidth} percentage={counter} />

            <CircleProgress size={size} strokeWidth={strokeWidth} percentage={counter} pos="absolute" inset={0} />
          </Box>

          <Flex align="center" direction="column" gap={2} justify="center" textAlign="center" pos="absolute">
            <Text fontSize="md" textStyle="uppercase">
              Match rate
            </Text>

            <Text textStyle="h1" fontSize={{ base: "7xl", md: "110px" }}>
              +{counter}%
            </Text>

            <BoostedText isActive={progressActive || progressComplete} />
          </Flex>
        </ChakraBox>
      </AnimatePresence>
    </Box>
  );
}
